/** @jsxImportSource @emotion/react */
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { SERVER_URL } from "../../config";
import FirstForm from "./FormSteps/FirstForm";
import SecondForm from "./FormSteps/SecondForm";
import ThirdForm from "./FormSteps/ThirdForm";

export enum SignupFormSection {
  Start = "Start",
  Create = "Create",
  TemporyPasscode = "TemporyPasscode",
  ChoosePasscode = "ChoosePasscode",
  ConfirmPasscode = "ConfirmPasscode",
  EnterPasscodeLearnistic = "EnterPasscodeLearnistic",
  TemporyPasscodeLearnistic = "TemporyPasscodeLearnistic",
  ChoosePasscodeLearnistic = "ChoosePasscodeLearnistic",
  ConfirmPasscodeLearnistic = "ConfirmPasscodeLearnistic",
}

export interface IFormSignup {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  appName: string;
  phone: string;
  domain: string;
  domainSuffix: string;
  sendFromName: string;
  temporaryPasscode: string[];
  password: string[];
  confirmPasscode: string[];
  choosePasscode: string[];
  origin: string;
  alias: string;
}

export interface IFormSignupValidation {
  isFirstNameValid: boolean;
  isLastNameValid: boolean;
  isEmailValid: boolean;
  isLoading: boolean;
  isAppNameValid: boolean;
  isPhoneValid: boolean;
  isDomainValid: boolean;
  isSendFromNameValid: boolean;
}

interface FormProps {
  selectedForm: SignupFormSection;
  setSelectedForm: React.Dispatch<React.SetStateAction<SignupFormSection>>;
  form: IFormSignup;
  setForm: React.Dispatch<React.SetStateAction<IFormSignup>>;
}

export default function Form({
  selectedForm,
  setSelectedForm,
  form,
  setForm,
}: FormProps) {
  const router = useRouter();
  const { email } = router.query;

  const [formValidation, setFormValidation] = useState<IFormSignupValidation>({
    isFirstNameValid: true,
    isLastNameValid: true,
    isEmailValid: true,
    isLoading: true,
    isAppNameValid: true,
    isPhoneValid: true,
    isDomainValid: true,
    isSendFromNameValid: true,
  });

  useEffect(() => {
    (async () => {
      if (email) {
        const response = await fetch(`${SERVER_URL}/auth/check_email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        });

        const responseObject = await response.json();

        if (responseObject.data.user) {
          setSelectedForm(SignupFormSection.Create);
          setForm({
            ...form,
            firstName: responseObject.data.user.firstName,
            lastName: responseObject.data.user.lastName,
            email: responseObject.data.user.email,
          });
        }
      }
    })();
  }, [email]);

  function handleChangeForm(
    key: string,
    value: string | string[],
    secondKey?: string,
    secondValue?: string
  ) {
    let newForm = { ...form };
    newForm[key] = value;

    if (secondKey && secondValue) {
      newForm[secondKey] = secondValue;
    }
    setForm(newForm);
  }

  function handleChangeFormValidation(key: string, value: boolean) {
    let newFormValidation = { ...formValidation };
    newFormValidation[key] = value;

    setFormValidation(newFormValidation);
  }

  function returnBody(screen: SignupFormSection) {
    if (selectedForm === SignupFormSection.Start)
      return (
        <FirstForm
          setSelectedForm={setSelectedForm}
          form={form}
          handleChangeForm={handleChangeForm}
          formValidation={formValidation}
          handleChangeFormValidation={handleChangeFormValidation}
        />
      );

    if (selectedForm === SignupFormSection.Create)
      return (
        <SecondForm
          setSelectedForm={setSelectedForm}
          form={form}
          handleChangeForm={handleChangeForm}
          formValidation={formValidation}
          handleChangeFormValidation={handleChangeFormValidation}
        />
      );

    return (
      <ThirdForm
        selectedForm={selectedForm}
        setSelectedForm={setSelectedForm}
        form={form}
        handleChangeForm={handleChangeForm}
      />
    );
  }

  return <>{returnBody(selectedForm)}</>;
}
